import React from 'react';
import { useForm } from 'react-hook-form';
import useLabels from '@/lib/hooks/swr/useLabels';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import useUsers from '@/lib/hooks/swr/useUsers';
import translate from '@/lib/intl/translate';
import type LabelSelectValue from '@/types/LabelSelectValue';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import AutomationType from '../constants/AutomationType';
import FormFields from '../constants/FormFields';
import TemporaryFormFields from '../constants/TemporaryFormFields';
import type FormDataType from '../types/FormDataType';
import mapAutomationToFormData from '../utils/mapAutomationToFormData';
import mapFormDataToAutomation from '../utils/mapFormDataToAutomation';
import useMajorGiftAutomations from './useMajorGiftAutomations';

const BASE_DEFAULT = {
  minDonation: null,
  maxDonation: null,
  type: AutomationType.TASK,
};

const useAutomationsForm = (automation: MajorGiftAutomation | null = null) => {
  const { data: labels }: { data: LabelSelectValue[] | undefined } =
    usePreselectedLabels(automation?.labelIds?.tags);
  const { data: users } = useUsers();

  const defaultValues = React.useMemo(() => {
    let newDefaults: FormDataType = { ...BASE_DEFAULT };
    if (automation) {
      newDefaults = mapAutomationToFormData({ ...BASE_DEFAULT, ...automation });
      if (automation.type === AutomationType.LABEL) {
        newDefaults[TemporaryFormFields.LABELS] = labels || [];
      }
      if (automation.type === AutomationType.TASK && automation.taskAssignee) {
        newDefaults[TemporaryFormFields.ASSIGNEE] =
          automation.taskAssignee.userId;
      }
    }
    return newDefaults;
  }, [automation, labels]);

  const formMethods = useForm<FormDataType>({ defaultValues });

  const { handleSubmit, reset } = formMethods;
  React.useMemo(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { create: createLabel } = useLabels();
  const getLabelIds = async (labelSelectValues: LabelSelectValue[]) => {
    const newLabels = await Promise.all(
      labelSelectValues.map((item) =>
        item.labelId
          ? item
          : createLabel({ labelName: item.labelName, labelType: 'tag' }),
      ),
    );
    return newLabels.map(({ labelId }) => labelId);
  };

  const { create, update } = useMajorGiftAutomations();

  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit(async (formData) => {
        const automationData = mapFormDataToAutomation(formData);
        if (automationData[FormFields.TYPE] === AutomationType.LABEL) {
          const newLabelIds = await getLabelIds(
            formData[TemporaryFormFields.LABELS] || [],
          );
          if (newLabelIds.length) {
            automationData.labelIds = { tags: newLabelIds };
          }
        }
        if (automationData[FormFields.TYPE] === AutomationType.TASK) {
          const developer = (users || []).find(
            ({ userId }) => userId === formData[TemporaryFormFields.ASSIGNEE],
          );
          if (developer) {
            automationData.taskAssignee = {
              userId: developer.userId,
              displayName: developer.displayName,
            };
          }
        }

        try {
          if (automation) {
            await update(automation.automationId, automationData);
          } else {
            await create(
              automationData as Omit<
                MajorGiftAutomation,
                'automationId' | 'ownerOrgId'
              >,
            );
          }
        } catch (err) {
          showErrorMessage({ message: translate('API_ERROR_UNKNOWN') });
        }
      })(e),
    [automation, handleSubmit, users],
  );

  return {
    formMethods,
    onSubmit,
  };
};

export default useAutomationsForm;
